import React, { useState, useEffect } from 'react';
import { useJobs } from '../hooks/useJobs';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import './Schedule.css';

/* ─── Robust Firestore Date Parser ─── */
const parseJobDate = (value) => {
  if (!value) return null;
  if (value && typeof value === 'object' && 'seconds' in value) {
    return new Date(value.seconds * 1000);
  }
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/* ─── Format date key as YYYY-MM-dd in UTC to avoid timezone shifts ─── */
const formatDateKey = (d) => {
  if (!d) return null;
  if (typeof d === 'string') {
    const match = d.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  if (typeof d === 'object' && 'seconds' in d) {
    const date = new Date(d.seconds * 1000);
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  if (d instanceof Date) {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return null;
};

/* ─── Generate each calendar day between two dates (UTC-based keys) ─── */
const eachDayBetween = (start, end) => {
  const startKey = formatDateKey(start);
  const endKey = formatDateKey(end);
  if (!startKey || !endKey || startKey > endKey) return [];
  const [sy, sm, sd] = startKey.split('-').map(Number);
  const [ey, em, ed] = endKey.split('-').map(Number);
  const days = [];
  let current = new Date(Date.UTC(sy, sm - 1, sd));
  const endDate = new Date(Date.UTC(ey, em - 1, ed));
  while (current <= endDate) {
    const y = current.getUTCFullYear();
    const m = String(current.getUTCMonth() + 1).padStart(2, '0');
    const d = String(current.getUTCDate()).padStart(2, '0');
    days.push(`${y}-${m}-${d}`);
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return days;
};

const Schedule = () => {
  const { jobs, loading } = useJobs();
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Calculate empty slots before the 1st day to align with correct weekday
  const firstDayOfWeek = monthStart.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const emptySlots = Array.from({ length: firstDayOfWeek }, (_, i) => `empty-${i}`);

  const jobsByDay = {};
  jobs.forEach((j) => {
    const startKey = formatDateKey(j.scheduledDate);
    if (startKey) {
      if (!jobsByDay[startKey]) jobsByDay[startKey] = [];
      jobsByDay[startKey].push(j);
    }
    if ((j.isMultiDay || j.scheduledEndDate) && j.scheduledEndDate) {
      const rangeDayKeys = eachDayBetween(j.scheduledDate, j.scheduledEndDate);
      rangeDayKeys.forEach((dKey) => {
        if (!jobsByDay[dKey]) jobsByDay[dKey] = [];
        if (!jobsByDay[dKey].find((job) => job.id === j.id)) {
          jobsByDay[dKey].push(j);
        }
      });
    }
  });

  const prevMonth = () => setCurrentMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));

  const weekdayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="page schedule">
      <div className="sched-header">
        <button className="sched-nav-btn" onClick={prevMonth}>←</button>
        <h2>{format(currentMonth, 'MMMM yyyy')}</h2>
        <button className="sched-nav-btn" onClick={nextMonth}>→</button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="sched-grid">
          {weekdayHeaders.map((d) => (
            <div key={d} className="sched-weekday">{d}</div>
          ))}
          {/* Empty cells before the 1st day */}
          {emptySlots.map((key) => (
            <div key={key} className="sched-day sched-day-empty" />
          ))}
          {days.map((day) => {
            const dayStr = format(day, 'yyyy-MM-dd');
            const dayJobs = jobsByDay[dayStr] || [];
            return (
              <div
                key={dayStr}
                className={`sched-day ${isToday(day) ? 'sched-today' : ''} ${dayJobs.length > 0 ? 'sched-has-jobs' : ''}`}
                onClick={() => {
                  if (dayJobs.length === 1) navigate(`/jobs/${dayJobs[0].id}`, { state: { returnTo: '/schedule' } });
                }}
              >
                <span className="sched-day-num">{format(day, 'd')}</span>
                {dayJobs.map((j) => (
                  <div key={j.id} className={`sched-job-dot ${(j.isMultiDay || j.scheduledEndDate) ? 'sched-job-multiday' : ''}`} style={{ backgroundColor: j.status === 'completed' ? '#10b981' : j.status === 'in-progress' ? '#3b82f6' : '#f59e0b' }}>
                    <span className="sched-job-title">{j.title}</span>
                    {(j.isMultiDay || j.scheduledEndDate) && <span className="sched-multiday-badge">↔</span>}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Schedule;
